import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Instagram, Mail, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    brand: "",
    budget: "",
    project_type: "",
    message: "",
    instagram: "",
};

const BUDGETS = ["< 500€", "500€ - 1500€", "1500€ - 5000€", "> 5000€"];
const PROJECTS = [
    "UGC ponctuel",
    "Campagne ads",
    "Partenariat long terme",
    "Autre",
];

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || form.message.length < 10) {
            toast.error("Merci de remplir nom, email et un message (10 caractères min).");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Merci ! Je reviens vers vous sous 48h.");
            setForm(initial);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Oups, une erreur est survenue. Réessayez ou écrivez-moi sur Instagram."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-bordeaux text-white"
        >
            <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                {/* Left */}
                <div className="lg:col-span-5">
                    <p className="eyebrow !text-gold mb-6">Travaillons ensemble</p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                        Vous avez un<br />
                        <span className="italic text-fuchsia">projet</span> en tête&nbsp;?
                    </h2>
                    <p className="mt-8 text-white/70 text-base md:text-lg max-w-md leading-relaxed">
                        Racontez-moi votre marque, vos objectifs, vos références. Je reviens vers
                        vous sous 48h avec une proposition sur-mesure.
                    </p>

                    <div className="mt-12 space-y-5">
                        <a
                            href="mailto:contact@melanie-creatrice.fr"
                            className="flex items-center gap-4 text-white hover:text-gold transition-colors group"
                            data-testid="contact-email"
                        >
                            <Mail className="w-4 h-4" />
                            <span className="text-sm">contact@melanie-creatrice.fr</span>
                        </a>
                        <a
                            href="https://www.instagram.com/melanie.creatrice/"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                            data-testid="contact-instagram"
                        >
                            <Instagram className="w-4 h-4" />
                            <span className="text-sm">@melanie.creatrice</span>
                        </a>
                        <a
                            href="https://www.tiktok.com/@melanie.creatrice"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                            data-testid="contact-tiktok"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                                <path d="M19.321 5.562a5.123 5.123 0 0 1-3.414-1.267 5.133 5.133 0 0 1-1.526-2.74 5.1 5.1 0 0 1-.078-.954h-3.29v13.28c0 1.2-.976 2.176-2.176 2.176a2.176 2.176 0 0 1 0-4.352c.12 0 .237.01.35.028V8.44a5.47 5.47 0 0 0-.35-.011c-3.016 0-5.465 2.449-5.465 5.465 0 3.015 2.449 5.464 5.465 5.464s5.465-2.449 5.465-5.464V8.287a8.397 8.397 0 0 0 4.902 1.566V6.56a5.1 5.1 0 0 1-.883-.058l.083-.94z" />
                            </svg>
                            <span className="text-sm">@melanie.creatrice</span>
                        </a>
                        <a
                            href="https://www.facebook.com/melanie.peyre.71?locale=fr_FR"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                            data-testid="contact-facebook"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                                <path d="M13.5 21.5v-7.75h2.6l.4-3H13.5V8.83c0-.87.24-1.46 1.49-1.46H16.6V4.69a21.43 21.43 0 0 0-2.31-.12c-2.29 0-3.86 1.4-3.86 3.96v2.22H7.8v3h2.63v7.75h3.07z" />
                            </svg>
                            <span className="text-sm">Mélanie Peyre</span>
                        </a>
                        <div className="flex items-center gap-4 text-white/70">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm">Bordeaux</span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
                >
                    <Field
                        label="Nom *"
                        id="name"
                        value={form.name}
                        onChange={(v) => set("name", v)}
                        testId="contact-input-name"
                    />
                    <Field
                        label="Email *"
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(v) => set("email", v)}
                        testId="contact-input-email"
                    />
                    <Field
                        label="Marque / société"
                        id="brand"
                        value={form.brand}
                        onChange={(v) => set("brand", v)}
                        testId="contact-input-brand"
                    />
                    <Field
                        label="Instagram"
                        id="instagram"
                        value={form.instagram}
                        onChange={(v) => set("instagram", v)}
                        placeholder="@votre-marque"
                        testId="contact-input-instagram"
                    />

                    <SelectField
                        label="Type de projet"
                        value={form.project_type}
                        onChange={(v) => set("project_type", v)}
                        options={PROJECTS}
                        testId="contact-select-project"
                    />
                    <SelectField
                        label="Budget estimé"
                        value={form.budget}
                        onChange={(v) => set("budget", v)}
                        options={BUDGETS}
                        testId="contact-select-budget"
                    />

                    <div className="md:col-span-2 mt-6">
                        <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">
                            Votre message *
                        </label>
                        <textarea
                            data-testid="contact-input-message"
                            rows={5}
                            value={form.message}
                            onChange={(e) => set("message", e.target.value)}
                            className="w-full bg-transparent border-0 border-b border-white/20 focus:border-gold focus:outline-none py-3 text-white placeholder-white/30 resize-none"
                            placeholder="Parlez-moi de votre marque, vos objectifs, votre deadline…"
                        />
                    </div>

                    <div className="md:col-span-2 mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <p className="text-xs text-white/40 max-w-sm">
                            En envoyant ce formulaire, vous acceptez d'être recontactée par Mélanie.
                            Vos données restent confidentielles.
                        </p>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="inline-flex items-center gap-3 bg-white text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-fuchsia hover:text-white transition-colors disabled:opacity-60"
                        >
                            {submitting ? "Envoi…" : "Envoyer le brief"}
                            <Send className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

function Field({ label, id, type = "text", value, onChange, placeholder, testId }) {
    return (
        <div className="mt-4">
            <label
                htmlFor={id}
                className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2"
            >
                {label}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || ""}
                data-testid={testId}
                className="w-full bg-transparent border-0 border-b border-white/20 focus:border-gold focus:outline-none py-3 text-white placeholder-white/30"
            />
        </div>
    );
}

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">
                {label}
            </label>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                    <button
                        key={o}
                        type="button"
                        onClick={() => onChange(value === o ? "" : o)}
                        data-testid={`${testId}-${o.replace(/\s|<|>|€/g, "")}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                            value === o
                                ? "bg-gold text-ink border-gold"
                                : "border-white/25 text-white/70 hover:border-white hover:text-white"
                        }`}
                    >
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}
